import { Alert, Button, Drawer, Space, Spin } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { VscSend } from 'react-icons/vsc'

import { Form, FormField } from '@/Core/UI/Form'
import { FieldCell, FieldGrid, FileFieldCell } from '@/Ship/Components/EntityCard'
import { StatusTag } from '@/Ship/Components/StatusTag'

import { LETTER_LABELS as L, LETTER_TEXTS as T } from '../Contracts/ILetter'
import { LETTER_STATUS_MAP, isLetterStatus } from '../Contracts/LetterStatus'
import { createLetterAddressInput } from './LetterAddressInput'

import type { LetterDrawerStore } from '../Stores/LetterDrawerStore'

/** Ширина шухляди — як у легасі (`CompanyEmailCrud.jsx:86-124`). */
const DRAWER_WIDTH = 600

const ADDRESS_FIELDS = new Set(['toWhom', 'cc', 'bcc'])

/** Статус у шапці — тим самим бейджем, що в колонці вкладки. */
const LetterStatusBadge = observer(({ store }: { store: LetterDrawerStore }) => {
    const status = store.letter?.status

    return isLetterStatus(status) ? (
        <StatusTag label={LETTER_STATUS_MAP[status].label} color={LETTER_STATUS_MAP[status].color} inline />
    ) : null
})

/**
 * Шухляда листа (`SPEC-013` §9.4–§9.5): поля в порядку легасі, «Відправити» в
 * шапці — коли лист редагується, він не відправлений і `_permissions.send`
 * дозволяє. Лист документа — «Відправити документи», без відкладеної дати.
 *
 * Над формою — **причина невідправлення**: `Alert` помилки «Помилка відправки
 * листа. {причина}». Це стан листа, а не пояснення, тож не `Hint`
 * (`.claude/rules/development.md`). Підпису в редакторі немає: його додає бек
 * під час відправки (§7.4) — про це підказка над таблицею вкладки.
 *
 * Шухляда своя, а не ядрова картка ресурсу: ядрові «Зберегти» /
 * «Редагувати» листу не потрібні (§9.4), а «⋮» ядра зламане (HANDOFF).
 */
export const LetterDrawer = observer(({ store }: { store: LetterDrawerStore }) => {
    // Поле адрес бере підказки з цього стору — один компонент на шухляду
    const [AddressInput] = useState(() => createLetterAddressInput(store))

    const form = store.form
    const letter = store.letter
    const title = store.variant === 'document' ? T.documentDrawerTitle : (letter?.subject?.trim() || T.drawerTitle)
    const sendError = letter?.status === 'failed' && typeof letter.sendError === 'string' ? letter.sendError.trim() : ''

    return (
        <Drawer
            title={
                <Space size={8} wrap>
                    <span>{title}</span>
                    <LetterStatusBadge store={store} />
                </Space>
            }
            open={store.isOpen}
            size={Math.min(DRAWER_WIDTH, window.innerWidth)}
            onClose={() => store.close()}
            maskClosable={!store.pending}
            destroyOnHidden
            extra={
                <Space>
                    <Button onClick={() => store.close()} disabled={store.pending}>
                        {T.close}
                    </Button>
                    {store.canSend ? (
                        <Button type="primary" icon={<VscSend />} loading={store.pending} onClick={() => void store.send()}>
                            {T.send}
                        </Button>
                    ) : null}
                </Space>
            }
        >
            {store.isLoading || !form ? (
                <Spin style={{ display: 'block', margin: '40px auto' }} />
            ) : (
                <Space orientation="vertical" style={{ width: '100%' }} size="middle">
                    {sendError !== '' ? (
                        <Alert type="error" showIcon title={`${T.sendFailed} ${sendError}`} />
                    ) : null}
                    <Form formContext={form}>
                        <FieldGrid>
                            {store.fieldNames.map((name) =>
                                name === 'attachments' ? (
                                    <FieldCell key={name} wide>
                                        <FileFieldCell formContext={form} name="attachments" label={L.attachments} />
                                    </FieldCell>
                                ) : (
                                    <FieldCell key={name} wide>
                                        {ADDRESS_FIELDS.has(name) && store.isEditable ? (
                                            <FormField name={name} input={AddressInput} />
                                        ) : (
                                            <FormField name={name} />
                                        )}
                                    </FieldCell>
                                )
                            )}
                        </FieldGrid>
                    </Form>
                </Space>
            )}
        </Drawer>
    )
})
